import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Joyride from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  createFirstTourHandler,
  createWarehouseTourHandler,
  createShipmentTourHandler,
  createChargeWalletTourHandler,
  createPriceCalculatorTourHandler,
  createDownloadAwbTourHandler
} from 'src/_store/entities/tutorial';
import {
  firstTourSteps,
  warehouseTourSteps,
  shipmentTourSteps,
  chargeWalletTourSteps,
  priceCalculatorTourSteps,
  downloadAwbTourSteps,
  TOUR_IDS
} from './tours';
import { useMediaQuery } from '@mui/material';
import CustomTooltip from './components/CustomTooltip';

// Map of tour IDs to their steps and handlers
const tourConfig = {
  [TOUR_IDS.FIRST_TOUR]: {
    steps: firstTourSteps,
    createHandler: createFirstTourHandler,
  },
  [TOUR_IDS.WAREHOUSE_TOUR]: {
    steps: warehouseTourSteps,
    createHandler: createWarehouseTourHandler,
  },
  [TOUR_IDS.SHIPMENT_TOUR]: {
    steps: shipmentTourSteps,
    createHandler: createShipmentTourHandler,
  },
  [TOUR_IDS.CHARGE_WALLET_TOUR]: {
    steps: chargeWalletTourSteps,
    createHandler: createChargeWalletTourHandler,
  },
  [TOUR_IDS.PRICE_CALCULATOR_TOUR]: {
    steps: priceCalculatorTourSteps,
    createHandler: createPriceCalculatorTourHandler,
  },
  [TOUR_IDS.DOWNLOAD_AWB_TOUR]: {
    steps: downloadAwbTourSteps,
    createHandler: createDownloadAwbTourHandler,
  },
};

export default function MapitTutorial() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const downMd = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const drawerElement = typeof window !== 'undefined' ? document.getElementById('drawer-app-id') : null;
  const isDrawerScrollable = drawerElement
    ? drawerElement?.scrollHeight > drawerElement?.clientHeight
    : false;

  const {
    run,
    stepIndex,
    tourId
  } = useSelector(state => state.entities.tutorial);

  // Get steps and handler based on tourId
  const { steps, handler } = useMemo(() => {
    const config = tourConfig[tourId];
    if (!config) {
      return { steps: [], handler: null };
    }
    return {
      steps: config.steps(downMd, isDrawerScrollable),
      handler: config.createHandler(config.steps(downMd, isDrawerScrollable)),
    };
  }, [tourId, downMd, isDrawerScrollable]);


  if (!tourId || !steps.length) {
    return null;
  }

  return (
    <Joyride
      callback={(data) => handler && dispatch(handler(data, navigate, downMd, isDrawerScrollable))}
      continuous
      run={run}
      stepIndex={stepIndex}
      scrollOffset={64}
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      disableOverlayClose
      disableCloseOnEsc
      tooltipComponent={CustomTooltip}
      floaterProps={{
        disableAnimation: true,
      }}
      locale={{
        back: t('tutorial.back'),
        close: t('tutorial.close'),
        last: t('tutorial.last'),
        next: t('tutorial.next'),
        open: t('tutorial.open'),
        skip: t('tutorial.skip'),
        nextLabelWithProgress: t('tutorial.nextLabelWithProgress', { stepIndex: stepIndex + 1, stepsLength: steps.length }),
      }}
      spotlightPadding={3}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: "#FC9E16",
        },
      }}
    />
  );
}
